import React, { FC, PropsWithChildren } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { SxProps, Theme, Typography } from '@mui/material';

interface AccordionProps {
  title: string | React.ReactNode;
  defaultExpanded?: boolean;
  expanded?: boolean;
  onChange?: () => void;
  /** Drops the card shadows, for accordions stacked on another surface such as a drawer. */
  flat?: boolean;
  sx?: SxProps<Theme>;
}

export const BhiAccordion: FC<PropsWithChildren<AccordionProps>> = props => {
  const { title, defaultExpanded, expanded, onChange, flat, sx, children } = props;

  return (
    <Accordion
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        // Clip summary/details hover backgrounds to the rounded corners above.
        overflow: 'hidden',
        boxShadow: (theme) => theme.customShadows.card,
        '&:before': {
          display: 'none', // Remove default MUI accordion line
        },
        '&:hover': {
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.08)',
          borderColor: 'grey.400',
        },
        '&.Mui-expanded': {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)',
          borderColor: 'grey.400',
        },
        '& .MuiAccordionSummary-root': {
          '&:hover': {
            backgroundColor: 'grey.100',
          },
        },
        '& .MuiAccordionDetails-root': {
          borderTop: '1px solid',
          borderColor: 'divider',
        },
        // borderColor is repeated because these replace the hover/expanded rules above wholesale.
        ...(flat && {
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none', borderColor: 'grey.400' },
          '&.Mui-expanded': { boxShadow: 'none', borderColor: 'grey.400' },
        }),
        ...sx
      }}
      expanded={expanded}
      onChange={onChange}
      defaultExpanded={defaultExpanded}
      disableGutters
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography component="span" sx={{ fontWeight: 'fontWeightMedium', color: 'grey.800' }}>
          {title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>{children}</AccordionDetails>
    </Accordion>
  );
};
